import { useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useGetShopMembersQuery,
  useRemoveShopMemberMutation,
} from "../features/shop/shopMemberApiSlice";
import { toTeamCard } from "../pages/Manage_Shop/Component/ShopManage/utils";
import { decodeId } from "../utils/idObfuscator";

export const useShopManageState = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { shopId: routeShopId } = useParams();
  const rawShopId =
    routeShopId ||
    location.state?.shopId ||
    localStorage.getItem("shopid") ||
    "";
  const shopId = decodeId(rawShopId) || rawShopId;

  const [selectedRemoval, setSelectedRemoval] = useState(null);
  const [deletedIds, setDeletedIds] = useState([]);
  const [show, setShow] = useState(false);
  const [secondmodal, setSecondmodal] = useState(false);
  const [isShown, setIsShown] = useState(false);
  const [isShownsecond, setIsShownsecond] = useState(false);
  const [actionError, setActionError] = useState("");

  const {
    data: teamResponse,
    isLoading: isTeamLoading,
    error: teamError,
  } = useGetShopMembersQuery(shopId, {
    skip: !shopId,
    refetchOnMountOrArgChange: true,
  });

  const [removeShopMember] = useRemoveShopMemberMutation();

  const rawMembers = teamResponse?.data?.members || [];

  const allMembers = useMemo(() => {
    if (!deletedIds.length) return rawMembers;
    return rawMembers.filter((member) => {
      const memberId = String(member._id || member.id || "");
      return !deletedIds.includes(memberId);
    });
  }, [rawMembers, deletedIds]);

  const teamMembers = useMemo(
    () => allMembers.filter((member) => member.status === "active"),
    [allMembers],
  );

  const members = useMemo(
    () =>
      teamMembers
        .filter((member) => member.role === "admin")
        .map(toTeamCard),
    [teamMembers],
  );

  const users = useMemo(
    () =>
      teamMembers
        .filter((member) => member.role === "member")
        .map(toTeamCard),
    [teamMembers],
  );

  const openRemoval = (type, id) => {
    setSelectedRemoval({ type, id });
    if (type === "user") setIsShownsecond(true);
    else setIsShown(true);
  };

  const closeRemoval = () => {
    setIsShown(false);
    setIsShownsecond(false);
    setSelectedRemoval(null);
  };

  const removeMember = async () => {
    if (!selectedRemoval) return;
    const targetRemoval = selectedRemoval;
    const targetId = String(targetRemoval.id);

    // Instantly close modal and optimistically remove from UI
    closeRemoval();
    setActionError("");
    setDeletedIds((prev) =>
      prev.includes(targetId) ? prev : [...prev, targetId],
    );

    try {
      await removeShopMember({
        shopId,
        membershipId: targetRemoval.id,
      }).unwrap();
    } catch (error) {
      // Revert optimistic removal on error
      setDeletedIds((prev) => prev.filter((id) => id !== targetId));
      setActionError(
        error?.data?.message || "Unable to remove this shop member.",
      );
    }
  };

  const removeUser = removeMember;

  return {
    navigate,
    location,
    shopId,
    members,
    users,
    teamMembers,
    isTeamLoading,
    teamError,
    actionError,
    show,
    setShow,
    secondmodal,
    setSecondmodal,
    isShown,
    isShownsecond,
    openRemoval,
    closeRemoval,
    removeMember,
    removeUser,
  };
};

export default useShopManageState;
